import { type ReadonlyVec2 } from 'gl-matrix';
import { vec2 } from 'gl-matrix';

import { type Vector2 as Vector2Interface } from '@canvas/vector-types';

import { Vector3 } from './vector-3.ts';

export interface Vector2Source {
  x: number;
  y: number;
}

export interface Vector2Data extends Vector2Source {
  type: 'Vector2';
}

export const vector2ToVec2 = (vector: Vector2Source | Vector2Data) =>
  vec2.fromValues(vector.x, vector.y);

/**
 * @deprecated
 */
export class Vector2 implements Vector2Interface, Vector2Data {
  public readonly type = 'Vector2';
  /**
   * @deprecated Prefer gl-matrix vec2. To aid migration, if `Vector2` is required, convert from vec2 via `Vector2.fromVec2` as late as possible.
   */
  public constructor(
    public x: number,
    public y: number,
  ) {}

  public add(xy: number): this {
    this.x += xy;
    this.y += xy;
    return this;
  }

  public addV(v: Vector2): this {
    this.x += v.x;
    this.y += v.y;
    return this;
  }

  public subV(v: Vector2): this {
    this.x -= v.x;
    this.y -= v.y;
    return this;
  }

  public withZ(z: number) {
    return new Vector3(this.x, this.y, z);
  }

  public clone() {
    return new Vector2(this.x, this.y);
  }

  static from(source: Vector2Source) {
    return new Vector2(source.x, source.y);
  }

  static fromVec2(data: ReadonlyVec2) {
    return new Vector2(data[0], data[1]);
  }

  static fromJSON(data: unknown) {
    if (
      // Ensure the data is not null, undefined, or a primitive
      data == null ||
      typeof data !== 'object' ||
      // Ensure the array has at least two elements
      !('0' in data) ||
      !('1' in data) ||
      // Ensure the elements are numbers
      typeof data[0] !== 'number' ||
      typeof data[1] !== 'number' ||
      // Ensure the elements are not NaN
      isNaN(data[0]) ||
      isNaN(data[1])
    ) {
      // Return a zero vector if the data is invalid
      return new Vector2(0, 0);
    }

    return new Vector2(data[0], data[1]);
  }

  public static equals(a: Vector2 | null | undefined, b: Vector2 | null | undefined) {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    return a.x === b.x && a.y === b.y;
  }
}

export const emptyVector2 = new Vector2(0, 0);

export const isVector2 = <T>(
  item: T,
): item is Exclude<T, Exclude<T, { type: 'Vector2' }>> => {
  return !!item && typeof item === 'object' && 'type' in item && item.type === 'Vector2';
};

/**
 * Compares coordinates at float32 storage precision. Null and undefined both mean absent.
 */
export function vector2EqualsFloat32(
  first: Vector2Source | null | undefined,
  second: Vector2Source | null | undefined,
): boolean {
  if (first == null || second == null) {
    return first == null && second == null;
  }
  return (
    Math.fround(first.x) === Math.fround(second.x) &&
    Math.fround(first.y) === Math.fround(second.y)
  );
}
